<script setup lang="ts">
import { defineAsyncComponent } from 'vue';
import { ref, onMounted } from 'vue';
import Teaser from './Teaser.vue';;
import MapSidebarContent from './MapSidebarContent.vue';;
const Map = defineAsyncComponent(() =>
    import('./Map.vue')
);
import { formatZip } from '../formatters';

const style = 'https://tiles.openfreemap.org/styles/liberty';

const props = defineProps<{
    baseurl: string,
    locations: string,
    showfilter: boolean,
}>();

const filteredLocations = ref();
const currentLocation = ref();
const filterZip = ref(-1);
const filterType = ref("Alle");
const filterPartOf = ref("Alle");

const types = ['Fachhandwerk', 'Großhandel', 'Hersteller'];
const partOf = ['Tag des Bades'];

const locations = JSON.parse(props.locations);

// {{{ filterLocations
const filterLocations = () => {
    const minZip = filterZip.value * 10000;
    const maxZip = minZip + 9999;

    filteredLocations.value = locations.filter((loc) => {
        let included = true;

        if (filterZip.value > -1) {
            if (loc.zip < minZip || loc.zip > maxZip) {
                included = false;
            }
        }
        if (filterType.value != 'Alle') {
            if (loc.type != filterType.value) {
                included = false
            }
        }
        if (filterPartOf.value != 'Alle') {
            if (loc.partof != filterPartOf.value) {
                included = false
            }
        }
        if (!included && currentLocation.value?.id == loc.id) {
            currentLocation.value = null;
        }
        return included;
    });

    if (!props.showfilter) {
        return;
    }
    const params = new URLSearchParams(window.location.search);
    if (filterZip.value != -1) {
        params.set("zip", filterZip.value);;
    } else {
        params.delete("zip");
    }
    if (filterType.value != 'Alle') {
        params.set("type", filterType.value);;
    } else {
        params.delete("type");
    }
    if (filterPartOf.value != 'Alle') {
        params.set("partof", filterPartOf.value);;
    } else {
        params.delete("partof");
    }
    const q = params.size == 0 ? '' : '?';
    window.history.replaceState({}, "", decodeURIComponent(`${window.location.pathname}${q}${params}${window.location.hash}`));
}
// }}}

onMounted(() => {
    if (!props.showfilter) {
        filterLocations();
    }

    const params = new URLSearchParams(window.location.search);

    const zip = params.get('zip') ? parseInt(params.get('zip'), 10) : -1;
    const type = params.get('type') ? params.get('type') : 'Alle';
    const partOf = params.get('partof') ? params.get('partof') : 'Alle';

    if (zip >= -1 && zip < 10) {
        filterZip.value = zip;
    }
    if (types.includes(type)) {
        filterType.value = type;
    }
    if (partOf.includes(partOf)) {
        filterPartOf.value = partOf;
    }

    filterLocations();
});
</script>

<template>
    <div class="map-filter" v-if="props.showfilter">
        <select v-model="filterZip" @change="filterLocations">
            <option value="-1">Alle Postleitzahlen</option>
            <option v-for="zip in [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]" :value="zip">{{ zip }}0000</option>
        </select>
        <select v-model="filterType" @change="filterLocations">
            <option value="Alle">Alle Typen</option>
            <option v-for="t in types" :value="t">{{ t }}</option>
        </select>
        <select v-model="filterPartOf" @change="filterLocations">
            <option value="Alle">Alle Ausstellungen</option>
            <option v-for="p in partOf" :value="p">{{ p }}</option>
        </select>
    </div>
    <div class="map-overview">
        <Transition>
            <div id="map-sidebar" class="exhibitor-profile active" v-if="currentLocation">
                <MapSidebarContent 
                    :link="props.showfilter"
                    :href="currentLocation.url"
                    :img="currentLocation.img != '' ? props.baseurl + currentLocation.img : ''"
                    :logo="currentLocation.logo != '' ? props.baseurl + currentLocation.logo : ''"
                    :title="currentLocation.company"
                    :text="currentLocation.teasertext"
                    :street="currentLocation.street"
                    :zip="formatZip(currentLocation.zip)"
                    :place="currentLocation.place"
                    :googlemapsurl="currentLocation.googlemapsurl"
                    :applemapsurl="currentLocation.applemapsurl"
                    :features="currentLocation.features"
                    :partof="currentLocation.partof"
                />
            </div>
        </Transition>
        <Map
            :locations="filteredLocations"
            :baseurl="props.baseurl"
            :showfilter="props.showfilter"
            v-model="currentLocation"
        />
    </div>
    <div class="newslist style-cards" id="exhibitor-profiles"  v-if="props.showfilter">
        <TransitionGroup tag="div" class="inner">
            <Teaser class="teaser" v-for="loc in filteredLocations" :key="loc.id"
                :href="loc.url"
                :img="loc.teaser != '' ? props.baseurl + loc.teaser : ''"
                :title="loc.company"
                :text="loc.teasertext"
                :extraText="formatZip(loc.zip) + ' ' + loc.place"
                :partof="loc.partof"
            >
            </Teaser>
        </TransitionGroup>
    </div>
</template>

<style lang="scss">
@use "../../sass/modules/_map-vue";

@keyframes entry-pulse {
    0% {
        transform: scale(1);
        opacity: 1;
    }
    100% {
        transform: scale(3);
        opacity: 0;
    }
}

</style

<!-- vim:set sw=4 sts=4 fdm=marker : -->
